import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Chip,
  IconButton,
  TextField,
  CircularProgress,
  Snackbar,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StarIcon from '@mui/icons-material/Star';
import FaceIcon from '@mui/icons-material/Face';
import { getCapturedImageDisplayUrl } from '../../services/s3/captureUrlService';
import { identifyAsPerson, identifySOIAsPerson } from '../../services/api/unknownFacesService';
import { listDatasets } from '../../services/api/datasetService';

/**
 * Extract camera/owner info from S3 key
 * S3 key format: public/captures/YYYY-MM-DD/capture-id/{owner}/success/image.jpg
 */
function extractCameraInfo(s3Key) {
  if (!s3Key) return null;
  const parts = s3Key.split('/');
  // Find owner (after capture-id, before success/unknown/associated)
  const ownerIndex = parts.findIndex(p => p === 'success' || p === 'unknown' || p === 'associated') - 1;
  if (ownerIndex >= 0 && ownerIndex < parts.length) {
    return parts[ownerIndex];
  }
  return null;
}

/**
 * UnknownFaceDetailsDialog - Shows details of an unknown face with sightings and actions
 */
export default function UnknownFaceDetailsDialog({ open, face, onClose, onRefresh }) {
  const [identifyDialogOpen, setIdentifyDialogOpen] = useState(false);
  const [personId, setPersonId] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [group, setGroup] = useState('');
  const [dataset, setDataset] = useState('');
  const [datasets, setDatasets] = useState([]);
  const [loadingDatasets, setLoadingDatasets] = useState(false);
  const [identifying, setIdentifying] = useState(false);
  const [promotionReason, setPromotionReason] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Load datasets when identify dialog opens
  useEffect(() => {
    if (identifyDialogOpen) {
      setLoadingDatasets(true);
      listDatasets({ includeInactive: false })
        .then(data => {
          setDatasets(data);
          if (data.length > 0) {
            setDataset(data[0].dataset_id); // Default to first dataset
          }
        })
        .catch(error => {
          console.error('[UnknownFaceDetailsDialog] Error loading datasets:', error);
          setSnackbar({ open: true, message: 'Failed to load datasets', severity: 'error' });
        })
        .finally(() => setLoadingDatasets(false));
    }
  }, [identifyDialogOpen]);

  if (!face) return null;

  const handleIdentify = async () => {
    if (!dataset) {
      setSnackbar({ open: true, message: 'Please select a dataset', severity: 'error' });
      return;
    }

    if (!firstName.trim()) {
      setSnackbar({ open: true, message: 'Please enter first name', severity: 'error' });
      return;
    }

    if (!promotionReason.trim()) {
      setSnackbar({ open: true, message: 'Please enter promotion reason', severity: 'error' });
      return;
    }

    try {
      setIdentifying(true);

      // Detect if this is a SOI person or Unknown face
      const isSOIPerson = face.status === 'PROMOTED_TO_SOI';
      const faceId = isSOIPerson ? face.unknown_face_id : face.unknown_face_id; // SOI uses soi_person_id stored in unknown_face_id field

      console.log('[UnknownFaceDetailsDialog] Starting promotion:', {
        faceId,
        isSOIPerson,
        status: face.status,
        dataset,
        firstName,
        lastName,
        group,
        promotionReason
      });

      let result;
      if (isSOIPerson) {
        // SOI → Known: Use identifySOIAsPerson
        result = await identifySOIAsPerson(
          faceId,
          dataset,
          firstName.trim(),
          lastName ? lastName.trim() : '',
          group ? group.trim() : '',
          promotionReason.trim()
        );
        console.log('[UnknownFaceDetailsDialog] SOI promotion result:', result);
      } else {
        // Unknown → Known: Use identifyAsPerson (person ID auto-generated by backend)
        result = await identifyAsPerson(
          faceId,
          null,
          dataset,
          firstName.trim(),
          lastName ? lastName.trim() : '',
          group ? group.trim() : '',
          promotionReason.trim()
        );
        console.log('[UnknownFaceDetailsDialog] Unknown promotion result:', result);
      }

      // Clear form
      setFirstName('');
      setLastName('');
      setGroup('');
      setDataset('');
      setPromotionReason('');
      setIdentifyDialogOpen(false);

      console.log('[UnknownFaceDetailsDialog] Refreshing list...');
      // Refresh list first
      if (onRefresh) {
        await onRefresh();
      }
      console.log('[UnknownFaceDetailsDialog] List refreshed, closing dialog');

      setSnackbar({ open: true, message: 'Successfully promoted to known person', severity: 'success' });

      // Close parent dialog immediately
      onClose();
    } catch (error) {
      console.error('[UnknownFaceDetailsDialog] Error identifying:', error);
      setSnackbar({ open: true, message: `Failed to promote person: ${error.message}`, severity: 'error' });
    } finally {
      setIdentifying(false);
    }
  };

  function formatDate(isoString) {
    if (!isoString) return '—';
    const date = new Date(isoString);
    const day = date.getDate();
    const month = date.toLocaleDateString('en-GB', { month: 'short' });
    const year = date.getFullYear();
    const time = date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
    return `${day} ${month} ${year} ${time}`;
  }

  function getStatusColor(status) {
    switch (status) {
      case 'SOI': return 'warning';
      case 'IDENTIFIED': return 'success';
      default: return 'default';
    }
  }

  return (
    <>
      <Dialog
        open={open && !identifyDialogOpen}
        onClose={onClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="h6" sx={{ fontFamily: 'monospace' }}>
                {face.unknown_face_id}
              </Typography>
              <Chip
                label={face.status || 'UNKNOWN'}
                color={getStatusColor(face.status)}
                size="small"
              />
            </Box>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          {/* Header: First sighting image + Stats */}
          <Box sx={{ display: 'flex', gap: 3, mb: 3, pb: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
            {/* First Sighting Image */}
            {face.sightings && face.sightings.length > 0 && (
              <Box
                component="img"
                src={getCapturedImageDisplayUrl(face.sightings[0].capture_s3_key)}
                alt="First sighting"
                sx={{
                  width: 120,
                  height: 120,
                  objectFit: 'cover',
                  borderRadius: 1,
                  border: '2px solid',
                  borderColor: 'divider'
                }}
              />
            )}

            {/* Stats Grid - Condensed 2x2 */}
            <Grid container spacing={2} sx={{ flex: 1, alignContent: 'start' }}>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Sightings</Typography>
                <Typography variant="h6">{face.sighting_count || 0}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Unique Days</Typography>
                <Typography variant="h6">{face.unique_days?.length || 0}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">First Seen</Typography>
                <Typography variant="body2">{formatDate(face.first_seen)}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Last Seen</Typography>
                <Typography variant="body2">{formatDate(face.last_seen)}</Typography>
              </Grid>
            </Grid>
          </Box>

          {/* Audit Trail - Most Recent First */}
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>Audit Trail</Typography>

          {/* Identified (most recent) */}
          {face.status === 'IDENTIFIED' && face.identification_metadata && (
            <Box sx={{ mb: 2, p: 2, bgcolor: 'success.50', borderRadius: 1, borderLeft: '4px solid', borderColor: 'success.main' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <CheckCircleIcon sx={{ fontSize: 18, color: 'success.main' }} />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Identified as Known Person
                </Typography>
              </Box>
              <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                {face.identification_metadata.first_name} {face.identification_metadata.last_name || ''}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                ID: {face.identification_metadata.person_id}
              </Typography>
              {face.identification_metadata.identification_reason && (
                <Box sx={{ mb: 1, p: 1, bgcolor: 'background.paper', borderRadius: 0.5 }}>
                  <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                    "{face.identification_metadata.identification_reason}"
                  </Typography>
                </Box>
              )}
              <Typography variant="caption" color="text.secondary">
                {formatDate(face.identification_metadata.identified_at)} • {face.identification_metadata.identified_by_handle || 'System'}
              </Typography>
            </Box>
          )}

          {/* Promoted to SOI */}
          {face.promoted_to_soi_at && (
            <Box sx={{ mb: 2, p: 2, bgcolor: 'warning.50', borderRadius: 1, borderLeft: '4px solid', borderColor: 'warning.main' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <StarIcon sx={{ fontSize: 18, color: 'warning.main' }} />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Promoted to Subject of Interest
                </Typography>
              </Box>
              {face.promotion_reason && (
                <Box sx={{ mb: 1, p: 1, bgcolor: 'background.paper', borderRadius: 0.5 }}>
                  <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                    "{face.promotion_reason}"
                  </Typography>
                </Box>
              )}
              <Typography variant="caption" color="text.secondary">
                {formatDate(face.promoted_to_soi_at)} • {face.promoted_by_handle || 'System'}
              </Typography>
            </Box>
          )}

          {/* First Detected */}
          <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1, borderLeft: '4px solid', borderColor: 'grey.400' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <FaceIcon sx={{ fontSize: 18, color: 'grey.600' }} />
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Face Detected
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary">
              {formatDate(face.first_seen)} • {
                face.sightings && face.sightings.length > 0
                  ? (extractCameraInfo(face.sightings[0].capture_s3_key) || 'Unknown camera')
                  : 'System'
              }
            </Typography>
          </Box>

          {/* Sightings */}
          <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 1 }}>
            Sightings ({face.sightings?.length || 0})
          </Typography>
          <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
            {face.sightings && face.sightings.length > 0 ? (
              <Grid container spacing={1}>
                {face.sightings.map((sighting, idx) => (
                  <Grid item xs={12} sm={6} md={4} key={idx}>
                    <Box
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        overflow: 'hidden',
                        '&:hover': { borderColor: 'primary.main' }
                      }}
                    >
                      {/* Image */}
                      <Box
                        component="img"
                        src={getCapturedImageDisplayUrl(sighting.capture_s3_key)}
                        alt={`Sighting ${idx + 1}`}
                        sx={{
                          width: '100%',
                          height: 120,
                          objectFit: 'cover',
                          bgcolor: 'grey.100'
                        }}
                      />
                      {/* Details */}
                      <Box sx={{ p: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {formatDate(sighting.timestamp)}
                        </Typography>
                        {sighting.similarity && (
                          <Typography variant="caption" color="text.secondary">
                            Similarity: {sighting.similarity.toFixed(1)}%
                          </Typography>
                        )}
                        {sighting.co_occurred_with && sighting.co_occurred_with.length > 0 && (
                          <Typography variant="caption" color="primary" sx={{ display: 'block', mt: 0.5 }}>
                            With {sighting.co_occurred_with.length} known person(s)
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                No sightings recorded
              </Typography>
            )}
          </Box>
        </DialogContent>

        <DialogActions>
          {(face.status === 'PROMOTED_TO_SOI') && (
            <Button
              startIcon={<PersonAddIcon />}
              variant="contained"
              color="primary"
              onClick={() => setIdentifyDialogOpen(true)}
            >
              Identify as Person (SOI → Known)
            </Button>
          )}
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Identify Dialog */}
      <Dialog
        open={identifyDialogOpen}
        onClose={() => !identifying && setIdentifyDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Identify as Known Person</DialogTitle>
        <DialogContent>
          {!loadingDatasets && datasets.length === 0 ? (
            <Box sx={{ p: 2, bgcolor: 'error.50', borderRadius: 1, border: '1px solid', borderColor: 'error.main' }}>
              <Typography variant="body2" color="error" sx={{ fontWeight: 600, mb: 1 }}>
                No Datasets Available
              </Typography>
              <Typography variant="body2" color="text.secondary">
                You must create at least one active dataset before identifying unknown faces.
                Please create a dataset in the Dataset Management section first.
              </Typography>
            </Box>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Promote this unknown face to a known person. Person ID will be auto-generated.
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <FormControl fullWidth disabled={identifying || loadingDatasets}>
                  <InputLabel>Dataset</InputLabel>
                  <Select
                    value={dataset}
                    onChange={(e) => setDataset(e.target.value)}
                    label="Dataset"
                  >
                    {loadingDatasets ? (
                      <MenuItem disabled>Loading datasets...</MenuItem>
                    ) : datasets.length === 0 ? (
                      <MenuItem disabled>No active datasets available</MenuItem>
                    ) : (
                      datasets.map((ds) => (
                        <MenuItem key={ds.dataset_id} value={ds.dataset_id}>
                          {ds.dataset_name || ds.dataset_id}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  label="First Name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="Enter first name"
                  disabled={identifying}
                  required
                  autoFocus
                />

                <TextField
                  fullWidth
                  label="Last Name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Enter last name"
                  disabled={identifying}
                />

                <TextField
                  fullWidth
                  label="Group"
                  value={group}
                  onChange={(e) => setGroup(e.target.value)}
                  placeholder="Enter group (optional)"
                  disabled={identifying}
                />

                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Promotion Reason"
                  value={promotionReason}
                  onChange={(e) => setPromotionReason(e.target.value)}
                  placeholder="Why are you identifying this person? (e.g., Verified identity, Matched to database, etc.)"
                  disabled={identifying}
                  required
                />
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIdentifyDialogOpen(false)} disabled={identifying}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleIdentify}
            disabled={identifying || !firstName.trim() || !dataset || !promotionReason.trim() || loadingDatasets}
            startIcon={identifying ? <CircularProgress size={16} /> : <PersonAddIcon />}
          >
            {identifying ? 'Identifying...' : 'Identify'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
